import { useDeleteFavoritesProductMutation } from '@graphql/mutations/__generated__/me.generated'
import { useCartQuery } from '@graphql/queries/__generated__/cart.generated'
import { useMeFavoritesProductsQuery, useMeQuery } from '@graphql/queries/__generated__/me.generated'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useQueryParams } from 'use-query-params'
import { MetaWrapper } from '../../components/Meta'
import ProductDetailsDialog from '../../components/Product/ProductDetailsDialog'
import CartLineEditPanel from './components/CartLineEditPanel'
import Page from './Page'
import { CartParams } from './types'
import { CartTypeEnum } from '../../../generated/gqlTypes'
import { useReactiveVar } from '@apollo/client'
import { isWholesaleModeVar } from '../../../apolloClient'
import { Redirect } from 'react-router-dom'
import { WHOLESALE_CART_URL } from '../../urls'
import { useTranslation } from 'react-i18next'

const PAGINATE_FAVORITES = 3

const View: React.FC = ({}) => {
  const { t } = useTranslation()
  const [params, setParams] = useQueryParams(CartParams)
  const { enqueueSnackbar } = useSnackbar()
  const [lineLoading, setLineLoading] = useState<number | null>(null)
  const [productDialogData, setProductDialogData] = useState<{
    pk: string | number
    slug: string
  } | null>(null)
  const isWholesaleMode = useReactiveVar(isWholesaleModeVar)
  const meQuery = useMeQuery()
  const cartQueryResult = useCartQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { cartType: CartTypeEnum.Default, includeLinesByCategory: true },
  })

  const favoritesQueryResult = useMeFavoritesProductsQuery({
    variables: {
      limit: PAGINATE_FAVORITES,
      offset: ((params.favoritesPage || 1) - 1) * PAGINATE_FAVORITES,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (!data?.me?.wishedProducts.results.length && params.favoritesPage > 1) {
        setParams({ favoritesPage: params.favoritesPage - 1 }, 'replaceIn')
      }
    },
    skip: isWholesaleMode,
  })
  const [removeFromFavorites] = useDeleteFavoritesProductMutation({
    awaitRefetchQueries: false,
    refetchQueries: ['MeFavoritesProducts', 'MeFavoritesCategoriesInfo'],
  })

  const handleSignIn = () => {
    setParams({ global_dialog_form: 'signIn' }, 'replaceIn')
  }
  const handleSignUp = () => {
    setParams({ global_dialog_form: 'signUp' }, 'replaceIn')
  }

  const onClickProduct = (id: number, slug: string) => {
    setProductDialogData({ pk: id, slug })
  }

  const handleRemoveFromFavorites = async (productPk: number) => {
    setLineLoading(productPk)
    try {
      await removeFromFavorites({
        variables: {
          pk: productPk,
        },
      })
      enqueueSnackbar(
        t('snackbarMessages.error.productRemovedFromFavorites', 'Product has been removed from favorites.'),
        {
          variant: 'error',
        }
      )
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLineLoading(null)
  }

  if (isWholesaleMode && !!meQuery.data?.me) {
    return <Redirect to={WHOLESALE_CART_URL} />
  }

  return (
    <>
      <MetaWrapper titleDetails={t('metaTitle.shoppingBag', 'Shopping Bag')}>
        <Page
          handleSignIn={handleSignIn}
          handleSignUp={handleSignUp}
          cartQueryResult={cartQueryResult}
          isAuthenticated={!!meQuery.data?.me}
          lineLoading={lineLoading}
          setLineLoading={setLineLoading}
          onClickProduct={onClickProduct}
          favoritesQueryResult={favoritesQueryResult}
          handleRemoveFromFavorites={handleRemoveFromFavorites}
          params={params}
          setParams={setParams}
          meQueryResult={meQuery}
        />
      </MetaWrapper>
      <ProductDetailsDialog spec={productDialogData} isOpen={!!productDialogData} setIsOpen={setProductDialogData} />
      <CartLineEditPanel setLoading={setLineLoading} cartType={CartTypeEnum.Default} />
    </>
  )
}

export default View
